import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import WindowStickerTemplate from '@/components/WindowStickerTemplate';
import WindowStickerTemplateV2 from '@/components/WindowStickerTemplateV2';
import { ImageUpload } from '@/components/ImageUpload';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const WindowSticker = () => {
  const { providerId: urlProviderId } = useParams();
  const [searchParams] = useSearchParams();
  const queryProviderId = searchParams.get('providerId');
  
  // Use providerId from URL params or query params
  const providerId = urlProviderId || queryProviderId;
  
  const [providerSlug, setProviderSlug] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [providerLogoUrl, setProviderLogoUrl] = useState('');
  const [customHeading, setCustomHeading] = useState('BOOK A SESSION ONLINE');
  const [instructionText, setInstructionText] = useState('Scan QR code to make a booking');
  const [backgroundColor, setBackgroundColor] = useState('#ffffff');
  const [selectedVersion, setSelectedVersion] = useState('v1');
  const [fontFamily, setFontFamily] = useState('Inter');
  const [titleSize, setTitleSize] = useState('text-3xl');

  // Auto-fill provider slug if providerId is available
  useEffect(() => {
    if (providerId && !providerSlug && !customUrl) {
      // You could fetch provider data here to get the actual slug
      // For now, we'll use the providerId as a fallback
      setCustomUrl(`https://wellsuites.com/provider/${providerId}`);
    }
  }, [providerId, providerSlug, customUrl]);

  // Popular Google Fonts options
  const fontOptions = [
    { value: 'Inter', label: 'Inter (Default)' },
    { value: 'Arial', label: 'Arial' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Oswald', label: 'Oswald' },
    { value: 'Source Sans Pro', label: 'Source Sans Pro' },
    { value: 'Raleway', label: 'Raleway' },
    { value: 'Poppins', label: 'Poppins' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'PT Sans', label: 'PT Sans' },
    { value: 'Playfair Display', label: 'Playfair Display' },
    { value: 'Merriweather', label: 'Merriweather' },
  ];

  // Title size options
  const titleSizeOptions = [
    { value: 'text-xl', label: 'Small' },
    { value: 'text-2xl', label: 'Medium' },
    { value: 'text-3xl', label: 'Large (Default)' },
    { value: 'text-4xl', label: 'Extra Large' },
    { value: 'text-5xl', label: 'XXL' },
  ];

  // Generate the booking URL based on provider slug or custom URL
  const getBookingUrl = () => {
    if (customUrl) return customUrl;
    if (providerSlug) return `https://wellsuites.com/provider/${providerSlug}`;
    if (providerId) return `https://wellsuites.com/provider/${providerId}`;
    return 'https://wellsuites.com';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Window Sticker Generator</h1>
          <p className="text-gray-600">
            Create a custom window sticker for your wellness practice that customers can scan to book appointments online.
          </p>
          {providerId && (
            <p className="text-sm text-green-600 mt-2">
              ✓ Provider ID detected: {providerId}
            </p>
          )}
        </div>

        <div className="space-y-8">
          {/* Configuration Panel */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Customize Your Sticker</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="version">Sticker Version</Label>
                  <Tabs value={selectedVersion} onValueChange={setSelectedVersion} className="mt-2">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="v1">Classic</TabsTrigger>
                      <TabsTrigger value="v2">Clean</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>

                <div>
                  <Label>Provider Logo (Optional)</Label>
                  <div className="mt-2">
                    <ImageUpload
                      onImageUploaded={setProviderLogoUrl}
                      currentImageUrl={providerLogoUrl}
                      bucketName="sticker-logos"
                      folder="provider-logos"
                      className="w-full"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    Upload your business logo to personalize the sticker
                  </p>
                </div>

                {selectedVersion === 'v2' && (
                  <>
                    <div>
                      <Label htmlFor="customHeading">Custom Heading</Label>
                      <Input
                        id="customHeading"
                        placeholder="e.g., Book Your Appointment"
                        value={customHeading}
                        onChange={(e) => setCustomHeading(e.target.value)}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Customize the main heading text on your sticker
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="instructionText">Instruction Text</Label>
                      <Input
                        id="instructionText"
                        placeholder="e.g., Scan to book your session"
                        value={instructionText}
                        onChange={(e) => setInstructionText(e.target.value)}
                      />
                      <p className="text-sm text-gray-500 mt-1">
                        Customize the instruction text below the QR code
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="fontFamily">Font Style</Label>
                      <Select value={fontFamily} onValueChange={setFontFamily}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select a font" />
                        </SelectTrigger>
                        <SelectContent>
                          {fontOptions.map((font) => (
                            <SelectItem key={font.value} value={font.value}>
                              {font.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-gray-500 mt-1">
                        Choose a font for your heading and instruction text
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="titleSize">Title Size</Label>
                      <Select value={titleSize} onValueChange={setTitleSize}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select title size" />
                        </SelectTrigger>
                        <SelectContent>
                          {titleSizeOptions.map((size) => (
                            <SelectItem key={size.value} value={size.value}>
                              {size.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-gray-500 mt-1">
                        Choose the size of your main heading text
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="backgroundColor">Background Color</Label>
                      <div className="flex items-center space-x-3 mt-2">
                        <input
                          type="color"
                          id="backgroundColor"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                        />
                        <Input
                          placeholder="#ffffff"
                          value={backgroundColor}
                          onChange={(e) => setBackgroundColor(e.target.value)}
                          className="flex-1"
                        />
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Choose a background color for your sticker
                      </p>
                    </div>
                  </>
                )}

                <div>
                  <Label htmlFor="providerSlug">Provider Slug (Optional)</Label>
                  <Input
                    id="providerSlug"
                    placeholder="e.g., your-wellness-center"
                    value={providerSlug}
                    onChange={(e) => setProviderSlug(e.target.value)}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    This will create a direct link to your provider page
                  </p>
                </div>

                <div>
                  <Label htmlFor="customUrl">Custom URL (Optional)</Label>
                  <Input
                    id="customUrl"
                    placeholder="https://your-booking-website.com"
                    value={customUrl}
                    onChange={(e) => setCustomUrl(e.target.value)}
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Override with your own booking website URL
                  </p>
                </div>

                <div className="pt-4 border-t">
                  <h4 className="font-medium mb-2">Current QR Code URL:</h4>
                  <p className="text-sm bg-gray-100 p-2 rounded break-all">
                    {getBookingUrl()}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Instructions</CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600">
                  <li>Choose your preferred sticker version</li>
                  <li>Upload your provider logo (optional)</li>
                  {selectedVersion === 'v2' && (
                    <>
                      <li>Customize your heading text</li>
                      <li>Customize your instruction text</li>
                      <li>Select your preferred font style</li>
                      <li>Choose your title size</li>
                      <li>Choose your background color</li>
                    </>
                  )}
                  <li>Customize your booking URL above</li>
                  <li>Preview your sticker below</li>
                  <li>Click "Print Sticker" when ready</li>
                  <li>Use high-quality sticker paper</li>
                  <li>Place in your window or reception area</li>
                </ol>
              </CardContent>
            </Card>
          </div>

          {/* Full Width Preview Panel */}
          <Card>
            <CardHeader>
              <CardTitle>Preview - {selectedVersion === 'v1' ? 'Classic Version' : 'Clean Version'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex justify-center">
                {selectedVersion === 'v1' ? (
                  <WindowStickerTemplate 
                    providerSlug={providerSlug}
                    qrCodeUrl={getBookingUrl()}
                  />
                ) : (
                  <WindowStickerTemplateV2 
                    providerSlug={providerSlug}
                    qrCodeUrl={getBookingUrl()}
                    providerLogoUrl={providerLogoUrl}
                    customHeading={customHeading}
                    instructionText={instructionText}
                    backgroundColor={backgroundColor}
                    fontFamily={fontFamily}
                    titleSize={titleSize}
                  />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default WindowSticker;
